import React, { useState } from "react";
import { Lock, Sparkles, ChevronDown } from "lucide-react";
import { usePlanCheckout } from "../pricing/usePlanCheckout";

// Minimal markdown: ### headers, **bold**, "- " bullets, paragraphs.
function renderInline(text, key) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") ? <strong key={`${key}-${i}`} className="text-white font-semibold">{p.slice(2, -2)}</strong> : <React.Fragment key={`${key}-${i}`}>{p}</React.Fragment>
  );
}

function renderMarkdown(md) {
  const out = [];
  md.trim().split(/\n{2,}/).forEach((block, i) => {
    const lines = block.split("\n").filter(Boolean);
    let body = lines;
    if (lines[0].startsWith("### ")) {
      out.push(<h3 key={`h-${i}`} className="font-heading text-xl sm:text-2xl text-white mt-6 first:mt-0">{lines[0].slice(4)}</h3>);
      body = lines.slice(1);
    }
    if (body.length === 0) return;
    if (body.every((l) => l.startsWith("- "))) {
      out.push(
        <ul key={`ul-${i}`} className="mt-3 space-y-2">
          {body.map((l, j) => (
            <li key={l} className="flex gap-2 text-sm text-[#E8DFD3] leading-relaxed"><span className="text-[#C8A96E]">◆</span><span>{renderInline(l.slice(2), `li-${i}-${j}`)}</span></li>
          ))}
        </ul>
      );
    } else {
      out.push(<p key={`p-${i}`} className="mt-3 text-sm text-[#E8DFD3] leading-relaxed">{renderInline(body.join(" "), `p-${i}`)}</p>);
    }
  });
  return out;
}

function Chip({ label, value, hint, testid, tone, icon }) {
  const border = tone === "good" ? "border-[#9CBF8E]/30" : tone === "bad" ? "border-[#C9744F]/30" : "border-white/10";
  return (
    <div className={`rounded-lg border ${border} bg-white/[0.03] px-3 py-2 min-w-0 flex items-center gap-2.5`} data-testid={testid}>
      {icon && <img src={`/zodiac/${icon.split(" ")[0].toLowerCase()}.png`} alt="" className="w-9 h-9 shrink-0 opacity-90" data-testid={`${testid}-icon`} />}
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#A89C98]">{label}</div>
        <div className="text-sm text-white font-medium truncate">{value}</div>
        {hint && <div className="text-[11px] text-[#A89C98] leading-snug mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

function AdvancedDetails({ u, c }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      <button onClick={() => setOpen((o) => !o)} data-testid="bazi-advanced-toggle" className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-[#A89C98] hover:text-[#C8A96E] transition-colors">
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} /> Advanced details · traditional BaZi terms
      </button>
      {open && (
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bazi-advanced">
          <Chip label="Day Master" value={u.bazi_day_master} testid="bazi-day-master" />
          <Chip label="Useful God" value={u.useful_god_element} testid="bazi-useful-god" />
          <Chip label="Life Gua" value={`${u.gua_number} · ${(u.auspicious_directions || []).join(" / ")}`} testid="bazi-gua" />
          <Chip label="Pillars" value={`${u.pillars.year} · ${u.pillars.day}`} hint={`${u.pillars.month} · ${u.pillars.hour}`} testid="bazi-branches" />
          <Chip label="City founding pillar" value={c.founding_pillar || "Bearing geomancy"} testid="bazi-city-founded" />
          <Chip label="City branch" value={c.earthly_branch_affinity} testid="bazi-city-branch" />
        </div>
      )}
    </div>
  );
}

export function BaziGuide({ guide }) {
  const { busy, purchase } = usePlanCheckout();
  if (!guide) return null;
  const u = guide.user_profile, c = guide.target_city, s = guide.scoring, f = guide.friendly || {};
  const delta = s.whole_score - s.base_score;
  return (
    <section className="mt-10 max-w-4xl mx-auto glass rounded-2xl p-5 sm:p-7 border border-[#C8A96E]/30" data-testid="bazi-guide">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E] flex items-center gap-1.5"><Sparkles size={11} /> Western planets + Chinese zodiac</div>
          <div className="font-heading text-2xl sm:text-3xl text-white mt-1">Your Chinese Zodiac match for {c.name}</div>
        </div>
        <div className="text-right">
          <div className="font-mono-scanner text-4xl text-[#C8A96E] leading-none" data-testid="bazi-whole-score">{s.whole_score}<span className="text-base text-[#A89C98]">/100</span></div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A89C98] mt-1">Western {s.base_score} {delta !== 0 && <span className={delta > 0 ? "text-[#9CBF8E]" : "text-[#C9744F]"}>{delta > 0 ? `+${delta}` : delta} zodiac</span>}</div>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-2">
        <Chip label="Your element" value={f.your_element} hint={f.your_element_meaning} icon={f.your_element} testid="bazi-your-element" />
        <Chip label="Your birth-year animal" value={f.year_animal} hint={f.year_animal_meaning} icon={f.year_animal} testid="bazi-year-animal" />
        <Chip label="Your birthday animal" value={f.day_animal} hint={f.day_animal_meaning} icon={f.day_animal} testid="bazi-day-animal" />
        <Chip label="Lucky directions" value={(f.lucky_directions || []).join(" & ")} hint="from where you were born" testid="bazi-lucky-directions" />
        <Chip label="Element that helps you" value={f.helper_element} hint={f.helper_element_meaning} icon={f.helper_element} tone="good" testid="bazi-helper-element" />
        <Chip label="Element that drains you" value={f.drain_element} hint={f.drain_element_meaning} icon={f.drain_element} tone="bad" testid="bazi-drain-element" />
        <Chip label={`${c.name}'s element`} value={f.city_element} hint={f.city_founded ? `founded ${f.city_founded}` : "by direction from you"} icon={f.city_element} testid="bazi-city-element" />
        <Chip label={`${c.name}'s animal`} value={f.city_animal} hint={f.city_animal_meaning} icon={f.city_animal} testid="bazi-city-animal" />
      </div>
      {f.growth_zone && (
        <div className="mt-4 rounded-lg border border-[#C9744F]/40 bg-[#C9744F]/10 px-4 py-2 text-xs text-[#E4B59C]" data-testid="bazi-crucible">Growth zone — {f.growth_zone}. Expect fast lessons here, not bad luck.</div>
      )}
      <div className="mt-5" data-testid="bazi-markdown">{renderMarkdown(guide.markdown)}</div>
      {guide.locked && (
        <button
          onClick={() => purchase("natal_blueprint")}
          disabled={busy === "natal_blueprint"}
          data-testid="bazi-unlock-btn"
          className="mt-6 w-full relative rounded-xl border border-[#C8A96E]/50 bg-[#C8A96E]/10 hover:bg-[#C8A96E]/20 transition-colors p-5 text-left"
        >
          <div className="blur-[5px] select-none text-sm text-[#E8DFD3]/70" aria-hidden="true">
            What to watch in {c.name} — animal clashes, the growth-zone pattern, and personal tips for your trip: where to go, what to skip, and how to spend your first days.
          </div>
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C8A96E] font-bold">
            <Lock size={13} /> {busy === "natal_blueprint" ? "Opening checkout…" : "Unlock What to Watch & Trip Tips · $4.40"}
          </div>
        </button>
      )}
      <AdvancedDetails u={u} c={c} />
    </section>
  );
}
